/* eslint-disable tailwindcss/no-custom-classname */

import { useEffect, useState } from 'react';

import { SkuT } from '@lambda/apis/src/catalog-v2/types';

import { defaultBreakpointsV2 } from '@/lib/common/breakpoints';

import i18n from '@/settings/i18n';

import Slider from '@/components/slider/Slider';
import { generateSavingsPercentage } from '@/components/v2/product/hooks/helpers';
import OfferCard from '@/components/v2/common/card/OfferCard';
import { generateOfferImage } from '@/components/v2/product/sections/main/left/image/helpers';
import Section from '@/components/commons/Section';

const t = i18n.init('business');

const Placeholder = () => (
  <Slider breakpoints={defaultBreakpointsV2} speed={300}>
    {[0, 1, 2, 3, 4, 5].map((i) => (
      <div
        key={i}
        className="mt-2 h-[265px] animate-pulse border border-[#F9F9F9] bg-gray-500"
      />
    ))}
  </Slider>
);

const PopularForBusinesses = () => {
  const [skus, setSkus] = useState<SkuT[] | null>(null);

  useEffect(() => {
    const fetchBestSellerSkus = async () => {
      try {
        const response = await fetch('/api/cart/best-seller-skus');

        if (!response.ok) {
          throw new Error(`Error: ${response.statusText}`);
        }

        const data: { items: SkuT[]; total: number } = await response.json();

        setSkus(data.items);
      } catch (err: any) {
        setSkus([]);
      }
    };

    fetchBestSellerSkus();
  }, []);

  if (skus != null && skus.length === 0) {
    return null;
  }

  return (
    <div className="reebelo-container">
      <Section>
        <h2 className="flex shrink-0 items-center text-lg font-bold md:text-xl">
          {t`Popular for businesses`}
        </h2>
        {skus != null ? (
          <div className="relative mt-2">
            <Slider breakpoints={defaultBreakpointsV2}>
              {skus.map((sku, index) => {
                const offer = sku?.offers?.[0];
                const skuProduct = sku?.product;

                if (!offer || !sku || !skuProduct) {
                  return null;
                }

                const compareAtPrice = sku.compareAtPrice ?? null;

                const percentageSaving = generateSavingsPercentage(
                  offer.price,
                  compareAtPrice,
                );

                const isFeatured = !!offer?.featured;

                return (
                  <OfferCard
                    index={index}
                    key={`best-sellers-skus-${sku.id}`}
                    link={`/collections/${encodeURIComponent(
                      skuProduct.slug,
                    )}?skuId=${encodeURIComponent(sku.id)}`}
                    image={generateOfferImage(sku, skuProduct)}
                    title={sku.title}
                    compareAtPrice={
                      compareAtPrice ? compareAtPrice / 100 : null
                    }
                    percentageSaving={percentageSaving}
                    price={offer.price / 100}
                    featured={isFeatured}
                  />
                );
              })}
            </Slider>
          </div>
        ) : (
          <Placeholder />
        )}
      </Section>
    </div>
  );
};

export default PopularForBusinesses;
